import React from 'react';
import Chip from '@material-ui/core/Chip';
import Tooltip from '@material-ui/core/Tooltip';
import { alpha, makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(theme => ({
  root: {
    height: 16,
    // The Backstage theme gives every chip a trailing margin, which pushes
    // whatever follows the chip away from it and the chip off the middle of
    // its line.
    margin: 0,
  },
  // Tinted in the colour - a light wash of it behind text and outline in the
  // colour itself.
  primary: {
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
  },
  // The bright green of the theme is too light to read as text on a light
  // page, and its dark shade too dark on a dark one, so the text and the
  // outline take whichever stands out from the page.
  success: {
    borderColor:
      theme.palette.type === 'dark'
        ? theme.palette.success.light
        : theme.palette.success.dark,
    color:
      theme.palette.type === 'dark'
        ? theme.palette.success.light
        : theme.palette.success.dark,
    backgroundColor: alpha(theme.palette.success.main, 0.05),
  },
  // The warning and error colours read well on both pages as they are.
  warning: {
    borderColor: theme.palette.warning.main,
    color: theme.palette.warning.main,
    backgroundColor: alpha(theme.palette.warning.main, 0.05),
  },
  error: {
    borderColor: theme.palette.error.main,
    color: theme.palette.error.main,
    backgroundColor: alpha(theme.palette.error.main, 0.05),
  },
  // For what is no property of the team but a fact about it, like its vuid:
  // in the colour of secondary text, so it does not compete with the rest.
  neutral: {
    borderColor: theme.palette.divider,
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.action.hover,
  },
  dashed: {
    borderStyle: 'dashed',
  },
  clickable: {
    cursor: 'pointer',
    font: 'inherit',
  },
  // The font size goes onto the label, where the Backstage theme sets its own
  // - on the chip it would be overruled and the word cut off by the height.
  // A lower-case word has next to nothing above its x-height, so centred on
  // its line box it looks as if it sat low: the line is as tall as the font
  // and padded below, which lifts the word by a pixel and fills the 14px the
  // chip has inside its border. A small chip pads its label by 8px a side,
  // which is wide for text this size.
  label: {
    fontSize: 11,
    lineHeight: '12px',
    paddingBottom: 2,
    paddingLeft: 5,
    paddingRight: 5,
  },
}));

/**
 * Props for {@link TinyChip}.
 *
 * @public
 */
export type TinyChipProps = {
  label: string;
  /** What the chip means, on hover. */
  tooltip: string;
  color: 'primary' | 'success' | 'warning' | 'error' | 'neutral';
  /** A dashed outline rather than a solid one. */
  dashed?: boolean;
  /**
   * Makes the chip a button. It stops the click there, so that a chip inside
   * a link or a row that is one does not follow it too.
   */
  onClick?: () => void;
};

/**
 * A tiny chip that marks what a team or a department is, the same size
 * wherever it is shown: small enough to sit next to a name in a tree row
 * without making the row taller.
 *
 * @public
 */
export const TinyChip = (props: TinyChipProps) => {
  const { label, tooltip, color, dashed, onClick } = props;
  const classes = useStyles();
  return (
    <Tooltip title={tooltip}>
      <Chip
        size="small"
        variant="outlined"
        label={label}
        // A real button, so that the keyboard reaches it and a card that
        // stretches its link over everything lifts it above that link.
        {...(onClick
          ? {
              component: 'button',
              onClick: (event: React.MouseEvent) => {
                event.preventDefault();
                event.stopPropagation();
                onClick();
              },
            }
          : {})}
        classes={{
          root: [
            classes.root,
            classes[color],
            dashed ? classes.dashed : '',
            onClick ? classes.clickable : '',
          ]
            .filter(Boolean)
            .join(' '),
          label: classes.label,
          labelSmall: classes.label,
        }}
      />
    </Tooltip>
  );
};
